import { useAuthentication } from '@bestseller-bit/frontend-community.utilities.authentication';
import { datadogRum } from '@datadog/browser-rum';
import { Alert, Link, Paper, Stack, Typography } from '@mui/material';
import { Container } from '@mui/system';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { isPopupsBlockedError, isUserCancelledError } from 'src/utils/msal/error-utils';

export const AuthenticateGuard: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [popupsBlocked, setPopupsBlocked] = useState(false);
    const { isAuthenticated, acquireIdToken } = useAuthentication();

    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [loginCancelled, setLoginCancelled] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);

    const tryLogin = useCallback(async () => {
        if (isLoggingIn) {
            return;
        }

        try {
            setIsLoggingIn(true);
            await acquireIdToken();
        } catch (err) {
            if (isUserCancelledError(err)) {
                setLoginCancelled(true);
            } else if (isPopupsBlockedError(err)) {
                setPopupsBlocked(true);
                setIsLoggingIn(false);
            } else {
                datadogRum.addError(err);
                setLoginFailed(true);
            }
        } finally {
            setIsLoggingIn(false);
        }
    }, [acquireIdToken, isLoggingIn]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Reset popupsBlocked when authentication state changes
    useEffect(() => {
        setPopupsBlocked(false);
        setLoginFailed(false);
    }, [isAuthenticated]);

    useEffect(() => {
        if (!loginCancelled) {
            tryLogin();
        }
    }, [tryLogin, loginCancelled]);

    if (popupsBlocked) {
        // Popups are blocked, show a warning message
        return (
            <Alert severity="warning" sx={{ margin: '2rem' }}>
                <Stack
                    sx={{
                        gap: '0.5rem',
                    }}
                >
                    <Typography>
                        It seems you are blocking popups. We rely on popups for signing you in. Please allow popups and
                        try again.
                    </Typography>
                    <Typography>
                        If you need help, please send a request via{' '}
                        <Link
                            target="_blank"
                            href="https://bestseller.service-now.com/bestsellersp?id=sc_cat_item&sys_id=8c2094411b55ac1046aff646464bcbd3&sysparm_category=0cba6c05dbf91410693e231cd39619a2&assignment_group=52724f32879fdd50fcd5cb790cbb3513"
                        >
                            ServiceNow
                        </Link>
                        .
                    </Typography>
                </Stack>
            </Alert>
        );
    }

    return (
        <>
            {isAuthenticated ? (
                children
            ) : (
                <Container maxWidth="sm">
                    <Paper
                        sx={{
                            mt: 4,
                            px: 2,
                            py: 8,
                            pb: 12,
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {loginFailed && (
                                <Alert severity="error" sx={{ width: '100%' }}>
                                    <Typography>
                                        An error occurred while trying to sign you in. Please try again later.
                                    </Typography>
                                </Alert>
                            )}
                            {!popupsBlocked &&
                                (loginCancelled ? (
                                    <Stack
                                        sx={{
                                            mt: 4,
                                            gap: 8,
                                        }}
                                    >
                                        <Typography variant="h1">Having trouble signing in?</Typography>
                                        <Stack
                                            sx={{
                                                gap: 2,
                                            }}
                                        >
                                            <Typography variant="h2">Get help</Typography>
                                            <Typography>
                                                If you need help, please send a request via{' '}
                                                <Link
                                                    target="_blank"
                                                    href="https://bestseller.service-now.com/bestsellersp?id=sc_cat_item&sys_id=8c2094411b55ac1046aff646464bcbd3&sysparm_category=0cba6c05dbf91410693e231cd39619a2&assignment_group=52724f32879fdd50fcd5cb790cbb3513"
                                                >
                                                    ServiceNow
                                                </Link>
                                                .
                                            </Typography>
                                        </Stack>
                                        <Stack>
                                            <Typography variant="h2">Log in again</Typography>
                                            <Typography>
                                                To login again,{' '}
                                                <Link href="#" onClick={() => globalThis.location.reload()}>
                                                    refresh the page
                                                </Link>
                                                .
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Typography
                                        sx={{
                                            mt: 4,
                                        }}
                                    >
                                        {isLoggingIn
                                            ? "You're currently signing in, please go to the sign in popup to continue."
                                            : "Please wait, you're being redirected to the sign-in page shortly."}
                                    </Typography>
                                ))}
                        </Stack>
                    </Paper>
                </Container>
            )}
        </>
    );
};
